import { shallowEqual, useSelector } from "react-redux";

import { type InputTableQuerySpec, type Organism } from "@/graphql.ts";
import {
  createTableSpecForRootEntityType,
  getTableDataSlice,
  type ITableDataState,
} from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { ENTITY_TYPE, TABLE_CONTEXT_TYPE, VIEW } from "@/settings/settings.ts";
import { useGenomicTableCollections } from "@/ui-modules/genome-viewer/hooks/useGenomicTableCollections.ts";

import { ButtonHeader } from "../ButtonHeader.js";

// ----------
export function AnalyticalDataButtonHeader(): JSX.Element {
  return <ButtonHeader viewType={VIEW.DATA_VIEW.type} hideModifyColumnsBtn={true} />;
}

// ----------
interface IChildEntitiesButtonHeaderProps {
  tableSpec: InputTableQuerySpec;
  getParentEntityName: () => string;
  hideAnalyzeBtn?: boolean;
  hideVariantGenomesBtn?: boolean;
}

export function ChildEntitiesButtonHeader(props: IChildEntitiesButtonHeaderProps): JSX.Element {
  return (
    <ButtonHeader
      viewType={VIEW.TABLE_VIEW.type}
      tableSpec={props.tableSpec}
      getParentEntityName={props.getParentEntityName}
      hideBlastBtn={props.hideAnalyzeBtn}
      hideVariantGenomesBtn={props.hideVariantGenomesBtn}
    />
  );
}

// ----------
interface IContentBrowserButtonHeaderProps {
  entityType: string;
  getParentEntityName?: () => string;
}

export function ContentBrowserButtonHeader(props: IContentBrowserButtonHeaderProps): JSX.Element {
  return (
    <ButtonHeader
      viewType={VIEW.CONTENT_BROWSER_VIEW.type}
      tableSpec={createTableSpecForRootEntityType(props.entityType)}
      hideExportBtn={false}
      hideBlastBtn={true}
      hideShowRelatedBtn={true}
      getParentEntityName={props.getParentEntityName}
    />
  );
}

// ----------
export function GenomeBrowserButtonHeader(): JSX.Element {
  const tableData = useSelector<IAppState, ITableDataState>((state) => state.tableData, shallowEqual);
  const genome = useSelector<IAppState, Organism>((state) => state.genomeBrowser.genome, shallowEqual);

  // TODO this calculation should happen only when the menu or popover opens, move to the dedicated components !

  const tablesCollection = useGenomicTableCollections();

  const tableSpecs = tablesCollection
    .map((collection) => getTableDataSlice(tableData, collection.tableSpec))
    .filter((dataTable) => {
      // saving option only visible if some selection is applied to the table
      return dataTable?.selectionModel.selectedIds.length > 0;
    })
    // it is important to NOT make use of the intermediate table spec, this is only used to get our hands to the data slice, which hosts the GENUINE table query spec
    .map((dataTable) => dataTable.tableQuerySpec);

  return (
    <ButtonHeader
      viewType={VIEW.GENOME_VIEW.type}
      tableSpecs={tableSpecs}
      hideCopyToClassicPortalBtn={true}
      getParentEntityName={() => genome?.name}
    />
  );
}

// ----------
interface IGroupButtonHeaderProps {
  tableQuerySpec: InputTableQuerySpec;
  getParentEntityName: () => string;
}

export function GroupButtonHeader(props: IGroupButtonHeaderProps): JSX.Element {
  return (
    <ButtonHeader
      viewType={VIEW.TABLE_VIEW.type}
      tableSpec={props.tableQuerySpec}
      getParentEntityName={props.getParentEntityName}
      hideBlastBtn={props.tableQuerySpec?.resultType === ENTITY_TYPE.MARKER}
      hideVariantGenomesBtn={true}
    />
  );
}

// ----------
interface ISummaryButtonHeaderProps {
  entity?: any; // not even mandatory if there is no entity to be saved e.g. Jobs View
  getEntityName?: () => string;
  colummnSelectionButton?: { tableQuerySpec: InputTableQuerySpec; tableData: ITableDataState };
}

export function SummaryButtonHeader(props: ISummaryButtonHeaderProps): JSX.Element {
  return (
    <ButtonHeader
      viewType={VIEW.ENTITY_SUMMARY_VIEW.type}
      entity={props.entity}
      hideModifyColumnsBtn={!props.colummnSelectionButton}
      showEditParentButton={props.entity && props.entity.groupType === ENTITY_TYPE.SAMPLE_PARENT}
      hideBlastBtn={
        !props.entity ||
        props.entity.groupType === ENTITY_TYPE.SAMPLE ||
        props.entity.groupType === ENTITY_TYPE.SAMPLE_PARENT ||
        props.entity.groupType === ENTITY_TYPE.MARKER ||
        props.entity.groupType === ENTITY_TYPE.EVENT
      }
      hideSaveBtn={!props.entity || props.entity.groupType === ENTITY_TYPE.EVENT}
      getParentEntityName={props.getEntityName}
      tableSpec={props.colummnSelectionButton?.tableQuerySpec}
      tableData={props.colummnSelectionButton?.tableData}
    />
  );
}

// ----------
export function UserGroupAdminButtonHeader(): JSX.Element {
  return (
    <ButtonHeader
      viewType={VIEW.USER_GROUP_ADMIN_VIEW.type}
      hideBlastBtn={true}
      hideModifyColumnsBtn={true}
      hideUserGroupsAssignmentBtn={true}
    />
  );
}

// ----------
interface IUserGroupAdminTableButtonHeaderProps {
  entityType: string;
}

export function UserGroupAdminTableButtonHeader(props: IUserGroupAdminTableButtonHeaderProps): JSX.Element {
  return (
    <ButtonHeader
      viewType={VIEW.USER_GROUP_ADMIN_VIEW.type}
      tableSpec={createTableSpecForRootEntityType(props.entityType, TABLE_CONTEXT_TYPE.ADMIN)}
      hideNewUserGroupBtn={true}
      hideBlastBtn={true}
    />
  );
}
